import { GraphicsMaterials } from 'src/app/digital-twin/graphics/graphics-materials';
import { Injectable } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import { DigitalTwinService } from '../digital-twin/digital-twin.service'
import { AssetManager } from './asset-manager'
import { GaugingElement } from '../platform/busines-data/SectionElements/InspectionElements/gaugingElement'
import { SurfaceElement } from '../platform/busines-data/SectionElements/StructureElements/SurfaceElement'
import { AssetInfoViewService } from '../template/asset-info-view/asset-info-view/asset-info-view.service'
import { AssetManagerDownloadedInspection } from './asset-manager-downloaded-inspection'
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model'
import { ClusterState } from '../cluster/cluster.state'
import { ElementStatus } from '../platform/busines-data/SectionElements/InspectionElements/elementStatus'
import { OfflineService, Util } from '@phdsoft/common'
import { AnomalyService } from '../admin/services/anomaly.service'
import { NotificationService } from '../signalr/notification.service';
import { InspectionPlanService } from '../inspection-plan/inspection-plan.service';
import { IInspectionPlan } from '../inspection-plan/inspection-plan.interface';
import { CriticalAreaElement } from '../platform/busines-data/CriticalAreaElement/CriticalAreaElement';
import { Graphics } from '../digital-twin/graphics/graphics';
import { Asset } from '../platform/busines-data/Framework/Asset';
import { Mutex } from 'async-mutex';
import { StructuralElement } from '../platform/busines-data/SectionElements/StructureElements/StructuralElement';
import { BasicElement } from '../platform/busines-data/SectionElements/BasicElement/BasicElement';
import { ElementInsertionService } from '../admin/services/element-insertion.service';
import { AssetManagerDispose } from './asset-manager-dispose';


/** Offline, and the plan's inspections were not saved on this device. */
export class PlanNotSavedOffline extends Error {
  constructor(readonly planId: string) {
    super(`Plan ${planId} was not saved for offline use.`);
  }
}

/** A row of one of the plan's own occurrences. */
function ownedRow(row: { InspectionOccurrenceId?: string }, owned: ReadonlySet<string>): boolean {
  return !!row.InspectionOccurrenceId && owned.has(row.InspectionOccurrenceId);
}

@Injectable({ providedIn: 'root' })
export class AssetManagerFilter {
  public currentRegionIds: any[]
  public currentClusterId: string;
  public inspectionPlanIds: string[] = []

  /**
   * The occurrences each downloaded plan owns, from its download: every point the plan's
   * download returns names its occurrences that are the plan's. A plan's actions (View
   * only, Highlight, Only filled, Only anomalies) pick the downloaded rows by them, not by
   * the plan's dates: plans of one type may overlap, so the rows dated in a plan's window
   * can be another plan's work. Downloading a plan again replaces its entry, so its rows
   * and its ownership always come from the same answer.
   */
  private readonly planWork = new Map<string, ReadonlySet<string>>();

  /**
   * Records what a plan's download says it owns. An empty download owns nothing, so the
   * plan is not downloaded again by every action. A download whose points do not carry the
   * field (a copy stored offline before the plan said so) leaves the plan unknown, and its
   * actions use its dates.
   */
  public recordPlanWork(planId: string, inspections: ReadonlyArray<{ PlanOccurrenceIds?: string[] }> | null | undefined): void {
    if (!planId || !Array.isArray(inspections)) return;
    const owned = inspections.filter(x => Array.isArray(x?.PlanOccurrenceIds));
    if (inspections.length > 0 && owned.length === 0) return;
    this.planWork.set(planId, new Set(owned.flatMap(x => x.PlanOccurrenceIds as string[])));
  }

  /** A downloaded plan's own occurrences; null when it was not downloaded with them. */
  public ownedOccurrences(planId: string): ReadonlySet<string> | null {
    return this.planWork.get(planId) ?? null;
  }
  /** The inspection ids each plan's download brought in, so a plan already in the twin is not fetched again. */
  private readonly planInspectionIds = new Map<string, string[]>()
  private selectionMutex = new Mutex();
  private elementsListToBeDeleted: any[] = []

  // This is used to prevent race conditions when setting the anomalies array... Not the most elegant solution, but the alternative is a bigger refactor
  private lastCastCurrentRegionIdsRequestTimestamp = 0;


  constructor(
    private assetManager: AssetManager,
    private assetManagerDownloadedInspection: AssetManagerDownloadedInspection,
    private digitalTwinService: DigitalTwinService,
    private inpectionPlanService: InspectionPlanService,
    private clusterState: ClusterState,
    private assetInfoViewService: AssetInfoViewService,
    private readonly anomalyService: AnomalyService,
    private notificationService: NotificationService,
    private offlineService: OfflineService,
    private graphics: Graphics,
    private elementInsertionService: ElementInsertionService,
    private materials: GraphicsMaterials,
    private assetManagerDispose: AssetManagerDispose,
  ) {

    this.assetInfoViewService.castCurrentRegionIds.subscribe((regionIds) => {
      this.currentRegionIds = regionIds
      if (!regionIds || !this.assetManager || !this.assetManager.asset) return

      const assetId = this.assetManager.asset.assetInfo.AssetId
      let loadedAnomalies = this.assetManager.asset.anomalies || []
      let hasAnomaliesFromRegions = regionIds.every(regionId => loadedAnomalies.some(anomaly => anomaly.regionId === regionId))

      if (!hasAnomaliesFromRegions) {
        let newlyLoadedAnomalies = []
        const currentTimestamp = Date.now()
        this.lastCastCurrentRegionIdsRequestTimestamp = currentTimestamp

        const promises = regionIds.map(regionId =>
          this.anomalyService.getAnomalyList(assetId, regionId).then(
            anomalies => {
              newlyLoadedAnomalies.push(...anomalies)
            },
            err => console.log("Error while getting anomalies; \n", err)
          )
        )

        // Wait for all requests to complete, then set the anomalies array
        Promise.all(promises).then(() => {
          // Only set if this is still the most recent request
          if (currentTimestamp === this.lastCastCurrentRegionIdsRequestTimestamp) {
            this.assetManager.asset.anomalies = newlyLoadedAnomalies
          }
        })
      }
    })
  }

  //#region inspection
  public async deleteFilterElements(startDate: Date, endDate: Date, types: string[], filterId: string, isCluster: boolean) {
    const result = await this.digitalTwinService.deleteInspectionsByFilters(filterId);
    this.clusterState.resetState();

    let updatedElementsDictionary: { [id: string]: ElementResultInfo; } = {}
    for (let element of result) {
      updatedElementsDictionary[element.id] = element
    }

    if (result) {
      this.assetManager.asset.InspectionCampaigns.forEach((inspectionCollection) => {
        inspectionCollection.Elements.forEach((element) => {
          const gaugingElement = element as GaugingElement
          for (const checkList of gaugingElement.checkList) {
            let formattedDate = new Date(new Date(checkList.Date).toDateString())
            if (this.currentRegionIds.includes(gaugingElement.regionId)) {
              if (
                formattedDate >= startDate &&
                formattedDate <= endDate &&
                (types.includes(gaugingElement.inspectionTypeId))
              ) {
                // gaugingElement.SurfaceDesign.visible = true
                // inspectionCollection.deleteElementByIdOnly(gaugingElement.regionId, gaugingElement.Guid);
                // break
                if (updatedElementsDictionary[gaugingElement.Guid]) {
                  gaugingElement.checkList = updatedElementsDictionary[gaugingElement.Guid].CheckList
                  // gaugingElement.updateMaterial(this.assetManager.referenceDate, this.defaultElementMaterial)
                  gaugingElement.elementResult.Element.updateMaterial(this.assetManager.referenceDate)
                } else {
                  this.assetManagerDispose.disposeInspectionElement(
                    gaugingElement.elementResult,
                    inspectionCollection.CollectionInfoModel.CollectionId,
                  );
                }
                break
              } else {
                // gaugingElement.SurfaceDesign.visible = false
              }
            }
          }
        })
        this.notificationService.addSuccessNotification("Deleted Filter Elements' Follow Ups")
      })
    }
  }

  public async deleteFilterProtectionElements(startDate: Date, endDate: Date, types: String[], filterId: string, isCluster: boolean) {
    let protectionElements: any = this.assetManager.asset.ProtectionElementsCollection.Elements
    for (let element of protectionElements) {
      let formattedDate = new Date(
        new Date(element.createdAt).toDateString(),
      )
      if (this.currentRegionIds.includes(element.regionId)) {
        if (
          formattedDate >= startDate &&
          formattedDate <= endDate &&
          types.includes(element.TypeId)
        ) {
          const tempElement = {
            Id: element.Guid,
            RegionIds: element.regionIds,
            createdAt: element.createdAt,
          }
          this.elementsListToBeDeleted.push(tempElement)
          this.assetManager.asset.ProtectionElementsCollection.deleteElementByIdOnly(element.regionId, element.Guid);
        }
      }
    }
    if (this.elementsListToBeDeleted.length > 0) {
      try {
        const deleteResult = (await this.elementInsertionService.deleteMultipleElements(this.assetManager.asset.assetInfo.AssetId, this.elementsListToBeDeleted)).toPromise()
      }
      catch (e) {
        console.log("e", e)
      }
    }
    this.notificationService.addSuccessNotification("Deleted Filter Elements' Follow Ups")
  }

  public async reApplyFilterElements(startDate: Date, endDate: Date, types: string[], filterId: string, isCluster: boolean, sectionsIds: any) {
    this.digitalTwinService.reapplyInspectionsByFilters(filterId, sectionsIds);
    //this.notificationService.addSuccessNotification("Reapply Filter Elements");
  }

  public async loadCurrentInspectionPlan(regionId: string) {
    const data = { AssetId: this.assetManager.asset.assetInfo.AssetId, RegionId: regionId, CurrentDate: this.assetManager.referenceDate.toJSON() }
    let result: IInspectionPlan[]
    if (window.navigator.onLine) {
      result = await this.inpectionPlanService.getCurrentInspectionPlanByRegionId(data).toPromise();
    }
    else {
      // Attention: more than one plan can be stored in inspectionPlan_assetId_regionId ; it is an array!
      let dbStoreData = await this.offlineService.getItem(`inspectionPlan_${this.assetManager.asset.assetInfo.AssetId}_${regionId}`);
      result = dbStoreData?.json
      this.assetManager.plans = result || [] // we set this for the offline case. While online we will set the open plans (not only current plans) elsewhere
    }


    if (result)
      for (const iterator of result) {
        this.downloadInspectionsFromPlan(iterator.id);
      }
  }

  public async fixScopeFilterElements(startDate: Date, endDate: Date, types: string[], filterId: string, isCluster: boolean) {
    const result = await this.digitalTwinService.FixScopeElementsInspectionsByFilters(filterId);
  }

  public async downloadInspectionsFromFilter(filterId: string, isCluster?: boolean) {
    let inspections: ElementResultInfo[] = []
    const elements: ElementResultInfo[] = await this.digitalTwinService.getInspectionsByFilters(filterId).toPromise();

    for (const element of elements) {
      if (element.CheckList.length > 0) {
        for (const checklist of element.CheckList) {
          if (isCluster) {
            if (checklist.Clusterized) {
              inspections.push(element)
              break;
            }
          } else {
            inspections.push(element)
            break;
          }
        }
      }
    }

    await this.assetManagerDownloadedInspection.loadInspectionElements(this.assetManager.asset, inspections, this.assetManager.getCurrentRegionId(), false, false, this.assetManager.referenceDate);
    // this.inspectionFilterReset()

    this.showLoadedInspections(inspections)

    this.assetManager.updateAllInspectionColor()
  }

  /**
   * Downloads inspections from a specific plan.
   * @param planId The ID of the plan to download inspections from.
   */
  public downloadInspectionsFromPlan(planId: string): void {
    this.loadInspectionsFromPlan(planId).catch(error => {
      console.error("Error downloading inspections from plan:", error);
    });
  }

  /**
   * Loads a plan's inspections into the twin — from the API online, from the offline store
   * otherwise — and resolves once they are in, with what was loaded. Rejects when the download
   * failed, so a caller that has to know (hands-free numbering a plan's points) can say so.
   */
  public async loadInspectionsFromPlan(planId: string): Promise<ElementResultInfo[]> {
    let inspections: ElementResultInfo[] | undefined;
    if (window.navigator.onLine) {
      inspections = await firstValueFrom(this.digitalTwinService.getInspectionsByPlans(planId));
    } else {
      const dbStoreData = await this.offlineService.getItem(`inspections_inspectionPlan_${planId}`);
      inspections = dbStoreData?.json;
      // Saving a region for offline stores only the plans current on that day, so a plan can be
      // missing here — which is not the same as a plan with no inspections.
      if (!inspections) throw new PlanNotSavedOffline(planId);
    }

    const loaded = inspections ?? [];
    this.recordPlanWork(planId, loaded as ReadonlyArray<{ PlanOccurrenceIds?: string[] }>);
    // Waits for the points to be drawn: drawing ends by repainting every point, which would
    // otherwise undo a Highlight (or filter) the caller applies once this resolves.
    await this.processInspections(loaded);
    this.planInspectionIds.set(planId, loaded.map(inspection => inspection.id));
    return loaded;
  }

  /**
   * True when this plan's inspections were loaded and every one of them is still in the twin, so
   * loading them again would fetch nothing new. Checking presence rather than only remembering the
   * plan catches points the twin has since disposed (a region reload, another asset).
   */
  public isPlanLoaded(planId: string): boolean {
    const ids = this.planInspectionIds.get(planId);
    if (!ids) return false;

    const present = new Set<string>();
    for (const campaign of this.assetManager.asset?.InspectionCampaigns ?? []) {
      for (const element of campaign.Elements ?? []) {
        present.add(element.Guid);
      }
    }
    return ids.every(id => present.has(id));
  }

  /**
   * Process the inspections: store the IDs, load them into the asset manager, and update the color.
   * @param inspections The inspections to process.
   */
  private async processInspections(inspections: ElementResultInfo[]): Promise<void> {
    inspections.forEach((inspection) => {
      this.inspectionPlanIds.push(inspection.id);
    });

    await this.assetManagerDownloadedInspection.loadInspectionElements(this.assetManager.asset, inspections, this.assetManager.getCurrentRegionId(), false, false, this.assetManager.referenceDate);

    // try {
    this.assetManager.updateAllInspectionColor()
    //window.navigator.onLine ? this.assetManager.updateAllInspectionColor() : this.showLoadedInspections(inspections);
    // } catch (error) {
    //   console.error("Error updating inspection color:", error);
    // }

    this.showLoadedInspections(inspections)

    this.assetManager.updateAllInspectionColor()
  }

  public showLoadedInspections(inspections: ElementResultInfo[]) {
    var ids = inspections.map(x => x.id);
    if (ids.length == 0 || ids == null) {
      return;
    }

    this.assetManager.asset.InspectionCampaigns.forEach((inspectionCollection) => {
      inspectionCollection.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement
        if (ids.includes(gaugingElement.Guid)) {
          gaugingElement.SurfaceDesign.visible = true
        }
      })
    })
  }

  public async showOnlyInspections(startDate: Date, endDate: Date, types: string[]) {
    this.assetManager.asset.InspectionCampaigns.forEach((inspectionCollection) => {
      inspectionCollection.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement

        if (gaugingElement.Guid == "985c1267e58e4834ac915d4ccd537046") {
          console.log(gaugingElement)
        }

        for (const checkList of gaugingElement.checkList) {
          let formattedDate = new Date(new Date(checkList.Date?.toString()?.replace('Z', ''))?.toDateString())
          if (
            formattedDate >= startDate &&
            formattedDate <= endDate &&
            (types.includes(gaugingElement.inspectionTypeId))
          ) {
            gaugingElement.SurfaceDesign.visible = true
            break
          }
          else {
            gaugingElement.SurfaceDesign.visible = false
          }
        }
      })
    })
  }

  /** `owned`: a plan's own occurrences; when given, its points are those, whatever their dates. */
  public async inspectionDateFilter(startDate: Date, endDate: Date, types: string[], isCluster: boolean, planId?: string, owned?: ReadonlySet<string> | null) {
    this.assetManager.asset.InspectionCampaigns.forEach((inspectionCollection) => {
      inspectionCollection.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement
        if (gaugingElement.elementResult.Active === false) {
          var inspectionEndDate = new Date(new Date(gaugingElement.elementResult.EndDate.toString().replace('Z', '')).toDateString())
          if (inspectionEndDate <= this.assetManager.referenceDate) {
            gaugingElement.SurfaceDesign.visible = false
          }
        }
        else
          if (owned) {
            gaugingElement.SurfaceDesign.visible = this.currentRegionIds.includes(gaugingElement.regionId)
              && gaugingElement.active
              && gaugingElement.checkList.some(row => !row.Clusterized && ownedRow(row, owned))
          } else if (planId) {
            if (this.inspectionPlanIds.indexOf(gaugingElement.elementResult.id) !== -1) {
              for (const checkList of gaugingElement.checkList) {
                let formattedDate = new Date(new Date(checkList.Date?.toString()?.replace('Z', ''))?.toDateString())
                if (this.currentRegionIds.includes(gaugingElement.regionId) && !checkList.Clusterized) {
                  if (
                    formattedDate >= startDate &&
                    formattedDate <= endDate &&
                    (types.includes(gaugingElement.inspectionTypeId) && gaugingElement.active)
                  ) {
                    gaugingElement.SurfaceDesign.visible = true
                    break
                  } else {
                    gaugingElement.SurfaceDesign.visible = false;
                  }
                }
              }
            } else {
              gaugingElement.SurfaceDesign.visible = false
            }
          } else if (!isCluster) {
            for (const checkList of gaugingElement.checkList) {
              let formattedDate = new Date(new Date(checkList.Date?.toString()?.replace('Z', ''))?.toDateString())
              if (this.currentRegionIds.includes(gaugingElement.regionId) && !checkList.Clusterized) {
                if (
                  formattedDate >= startDate &&
                  formattedDate <= endDate &&
                  (types.includes(gaugingElement.inspectionTypeId) && gaugingElement.active)
                ) {
                  gaugingElement.SurfaceDesign.visible = true
                  break
                } else {
                  gaugingElement.SurfaceDesign.visible = false;
                }
              } else {
                gaugingElement.SurfaceDesign.visible = false
              }
            }
          } else {
            for (const checkList of gaugingElement.checkList) {
              if (checkList.Clusterized) {
                let formattedDate = new Date(new Date(checkList.Date?.toString()?.replace('Z', ''))?.toDateString())
                if (this.currentRegionIds.includes(gaugingElement.regionId)) {
                  if (
                    formattedDate >= startDate &&
                    formattedDate <= endDate &&
                    (types.includes(gaugingElement.inspectionTypeId) && gaugingElement.active)
                  ) {
                    gaugingElement.SurfaceDesign.visible = true
                    break
                  } else {
                    gaugingElement.SurfaceDesign.visible = false
                  }
                }
              } else {
                gaugingElement.SurfaceDesign.visible = false
              }
            }
          }
      })
    })
  }

  /** `owned`: a plan's own occurrences; when given, the anomalies of those rows, whatever their dates. */
  public anomalyFilter(startDate: Date, endDate: Date, types: String[], isCluster: boolean, owned?: ReadonlySet<string> | null) {
    this.assetManager.asset.InspectionCampaigns.forEach((inspectionCollection) => {
      inspectionCollection.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement
        for (const checkList of gaugingElement.checkList) {
          let formattedDate = new Date(new Date(checkList.Date).toDateString())
          if (this.currentRegionIds.includes(gaugingElement.regionId)) {
            if (
              (owned
                ? ownedRow(checkList, owned)
                : formattedDate >= startDate && formattedDate <= endDate && types.includes(gaugingElement.inspectionTypeId)) &&
              checkList.AnomalyId != null &&
              checkList.AnomalyId != '00000000-0000-0000-0000-000000000000'
            ) {
              gaugingElement.SurfaceDesign.visible = true
              break
            } else {
              gaugingElement.SurfaceDesign.visible = false
            }
          }
        }
      })
    })
  }

  /** `owned`: a plan's own occurrences; when given, its points are those, whatever their dates. */
  public inspectionColorHighlight(startDate: Date, endDate: Date, types: String[], isCluster: boolean, inspectionIds: string[], owned?: ReadonlySet<string> | null) {
    this.assetManager.asset.InspectionCampaigns.forEach((inspectionCollection) => {
      inspectionCollection.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement
        for (const checkList of gaugingElement.checkList) {
          let formattedDate = new Date(new Date(checkList.Date?.toString()?.replace('Z', ''))?.toDateString())
          if (this.currentRegionIds.includes(gaugingElement.regionId)) {
            if (
              owned
                ? ownedRow(checkList, owned)
                : formattedDate >= startDate && formattedDate <= endDate && types.includes(gaugingElement.inspectionTypeId)
            ) {
              gaugingElement.SurfaceDesign.material = Util.isNullOrUndefined(inspectionIds) ? GraphicsMaterials.selectedBasicElementMaterial :
                inspectionIds.includes(gaugingElement.Guid) ? GraphicsMaterials.selectedBasicElementMaterial : (element as SurfaceElement).currentMaterial;
              gaugingElement.SurfaceDesign.visible = true
              gaugingElement.isHighlightedByFilter = true
              break
            } else {
              gaugingElement.SurfaceDesign.material = (element as SurfaceElement).currentMaterial
              gaugingElement.isHighlightedByFilter = false
            }
          }
        }
      })
    })

    // this.assetManager.asset.CriticalAreaCampaigns.forEach((criticalAreaCollection) => {
    //   criticalAreaCollection.Elements.forEach((element) => {
    //     const criticalAreaElement = element as CriticalAreaElement
    //     let formattedDate = new Date(new Date(criticalAreaElement.CriticalAreaProperties.DateStarted).toDateString())
    //     if (this.currentRegionIds.includes(criticalAreaElement.regionId)) {
    //       if (
    //         formattedDate >= startDate &&
    //         formattedDate <= endDate || inspectionIds?.includes(criticalAreaElement.CriticalAreaProperties.CriticalAreaId)
    //       ) {
    //         criticalAreaElement.SurfaceDesign.material = Util.isNullOrUndefined(inspectionIds) ? GraphicsMaterials.selectedBasicElementMaterial :
    //           inspectionIds.includes(criticalAreaElement.CriticalAreaProperties.CriticalAreaId) ? GraphicsMaterials.selectedBasicElementMaterial : (element as SurfaceElement).currentMaterial;
    //         criticalAreaElement.SurfaceDesign.visible = true
    //         criticalAreaElement.isHighlightedByFilterStructure = true
    //       }
    //       else {
    //         criticalAreaElement.SurfaceDesign.material = (element as SurfaceElement).currentMaterial
    //         criticalAreaElement.isHighlightedByFilterStructure = false
    //       }
    //     }
    //   })
    // })

    if (inspectionIds) {
      this.assetManager.asset.RepairCampaigns.forEach((repairCollection) => {
        repairCollection.Elements.forEach((element) => {
          const repairElement = element as StructuralElement
          let formattedDate = new Date(new Date(repairElement.date).toDateString())
          if (this.currentRegionIds.includes(repairElement.regionId)) {
            if (
              inspectionIds?.includes(repairElement.Guid)
            ) {
              repairElement.SurfaceDesign.material = GraphicsMaterials.selectedBasicElementMaterial
              repairElement.SurfaceDesign.visible = true
              repairElement.isHighlightedByFilterStructure = true
            }
            else {
              repairElement.SurfaceDesign.material = (element as SurfaceElement).currentMaterial
              repairElement.isHighlightedByFilterStructure = false
            }
          }
        })
      })
    }
  }

  /** `owned`: a plan's own occurrences; when given, the filled rows among those, whatever their dates. */
  public inspectionFilterByValue(startDate: Date, endDate: Date, types: String[], isCluster: boolean, owned?: ReadonlySet<string> | null) {
    this.assetManager.asset.InspectionCampaigns.forEach((inspectionCollection) => {
      inspectionCollection.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement
        for (const checkList of gaugingElement.checkList) {
          let formattedDate = new Date(new Date(checkList.Date?.toString()?.replace('Z', ''))?.toDateString())
          if (this.currentRegionIds.includes(gaugingElement.regionId)) {
            if (
              (owned
                ? ownedRow(checkList, owned)
                : formattedDate >= startDate && formattedDate <= endDate && types.includes(gaugingElement.inspectionTypeId)) &&
              checkList.Value != ''
            ) {
              gaugingElement.SurfaceDesign.visible = true
              break
            } else {
              gaugingElement.SurfaceDesign.visible = false
            }
          }
        }
      })
    })
  }

  public protectionFilterByValue(startDate: Date, endDate: Date, types: String[], isCluster: boolean) {
    this.assetManager.asset.ProtectionElementsCollection.Elements.forEach((element) => {
      const basicElement = element as BasicElement;
      //const gaugingElement = element as GaugingElement
      // for (const checkList of basicElement.checkList) {
      //   let formattedDate = new Date(new Date(checkList.Date).toDateString())
      //   if (this.currentRegionIds.includes(basicElement.regionId)) {
      //     if (
      //       formattedDate >= startDate &&
      //       formattedDate <= endDate &&
      //       (types.includes(element.TypeId))) &&

      //       checkList.Value != ''
      //     ) {
      //       basicElement.SurfaceDesign.visible = true
      //       break
      //     } else {
      //       basicElement.SurfaceDesign.visible = false
      //     }
      //   }
      // }
    })
  }

  public inspectionFilterReset() {
    this.assetManager.asset.InspectionCampaigns.forEach((inspectionCollection) => {
      inspectionCollection.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement
        if (gaugingElement.elementResult.Active === false) {
          var endDate = new Date(new Date(gaugingElement.elementResult.EndDate.toString().replace('Z', '')).toDateString())
          if (endDate <= this.assetManager.referenceDate)
            gaugingElement.SurfaceDesign.visible = false
        }
        else {
          gaugingElement.SurfaceDesign.visible = true
        }
        gaugingElement.SurfaceDesign.material = (element as SurfaceElement).currentMaterial
        gaugingElement.isHighlightedByFilter = false
      })
    })

    this.assetManager.asset.CriticalAreaCampaigns.forEach((criticalAreaCollection) => {
      criticalAreaCollection.Elements.forEach((element) => {
        const gaugingElement = element as CriticalAreaElement
        // gaugingElement.SurfaceDesign.visible = true
        gaugingElement.SurfaceDesign.material = (element as SurfaceElement).defaultMaterial
        gaugingElement.isHighlightedByFilterStructure = false
      })
    })
  }

  public inspectionElementsHide() {
    this.assetManager.asset.InspectionCampaigns.forEach((inspectionCollection) => {
      inspectionCollection.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement
        for (const checkList of gaugingElement.checkList) {
          gaugingElement.SurfaceDesign.visible = false
        }
      })
    })
  }

  public criticalAreaElementsHide() {
    this.assetManager.asset.CriticalAreaCampaigns.forEach((criticalAreaCollection) => {
      criticalAreaCollection.Elements.forEach((element) => {
        const criticalAreaElement = element as CriticalAreaElement
        criticalAreaElement.SurfaceDesign.visible = false
      })
    })
  }

  public inspectionDateFilterResults(startDate: Date, endDate: Date, types: String[], isCluster: boolean) {
    let elementsCount = 0
    this.assetManager.asset.InspectionCampaigns.forEach((inspectionCollection) => {
      inspectionCollection.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement
        for (const checkList of gaugingElement.checkList) {
          let formattedDate = new Date(new Date(checkList.Date).toDateString())
          if (this.currentRegionIds.includes(gaugingElement.regionId)) {
            if (
              formattedDate >= startDate &&
              formattedDate <= endDate &&
              (types.includes(gaugingElement.inspectionTypeId))
            ) {
              elementsCount++
              break
            }
          }
        }
      })
    })
    return elementsCount
  }

  public inspectionCountByStatus(startDate: Date, endDate: Date, status: ElementStatus, types: String[]) {
    let elementsCount = 0
    this.assetManager.asset.InspectionCampaigns.forEach((inspectionCollection) => {
      inspectionCollection.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement
        for (const checkList of gaugingElement.checkList) {
          let formattedDate = new Date(new Date(checkList.Date).toDateString())
          if (this.currentRegionIds.includes(gaugingElement.regionId)) {
            if (
              formattedDate >= startDate
              && formattedDate <= endDate
              && checkList.Status == status
              && (types.includes(gaugingElement.inspectionTypeId))
            ) {
              elementsCount++
              break
            }
          }
        }
      })
    })
    return elementsCount
  }

  public countLockedFollowups(startDate: Date, endDate: Date, types: String[]): number {
    let lockedFollowupsCount = 0;
    this.assetManager.asset.InspectionCampaigns.forEach((inspectionCollection) => {
      inspectionCollection.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement
        for (const checkList of gaugingElement.checkList) {
          let formattedDate = new Date(new Date(checkList.Date).toDateString())
          if (this.currentRegionIds.includes(gaugingElement.regionId)) {
            if (
              formattedDate >= startDate
              && formattedDate <= endDate
              && (types.includes(gaugingElement.inspectionTypeId))
              && checkList.IsLocked
            ) {
              const checklistsByDate = Util.groupBy(gaugingElement.checkList, c => new Date(c.Date).toDateString())
              for (const [key, value] of Object.entries(checklistsByDate)) {
                if (value && value[0]?.IsLocked) {
                  lockedFollowupsCount++
                }
              }
            }
          }
        }
      })
    })
    return lockedFollowupsCount
  }



  //#endregion

  //#region structure

  public structureFilter(startDate: Date, endDate: Date, types: String[], isCluster: boolean) {
    this.assetManager.asset.StructureCampaigns.forEach((structure) => {
      structure.Elements.forEach((structureElement) => {
        if (structureElement.properties) {
          let formattedDate = new Date(new Date(structureElement.properties.General.Collection.Date).toDateString())
          if (this.currentRegionIds.includes(structureElement.regionId)) {
            if (
              formattedDate >= startDate &&
              formattedDate <= endDate &&
              (types.includes(structureElement.TypeId.toString()) || isCluster)
            ) {
              structureElement.SurfaceDesign.visible = true
            } else {
              structureElement.SurfaceDesign.visible = false
            }
          }
        }
      })
    })
    this.assetManager.asset.RepairCampaigns.forEach((repairCampaign) => {
      repairCampaign.Elements.forEach((repairStructureElement) => {
        if (repairStructureElement.properties) {
          let formattedDate = new Date(new Date(repairStructureElement.properties.General.Collection.Date).toDateString())
          if (this.currentRegionIds.includes(repairStructureElement.regionId)) {
            if (
              formattedDate >= startDate &&
              formattedDate <= endDate &&
              (types.includes(repairStructureElement.TypeId.toString()) || isCluster)
            ) {
              repairStructureElement.SurfaceDesign.visible = true
            } else {
              repairStructureElement.SurfaceDesign.visible = false
            }
          }
        }
      })
    })
  }

  public structureFilterCount(startDate: Date, endDate: Date, types: String[], isCluster: boolean) {
    let counter = 0
    this.assetManager.asset.StructureCampaigns.forEach((structure) => {
      structure.Elements.forEach((structureElement) => {
        if (structureElement.TypeId !== "35d4384eae2e482abf11b5b81880cd5e" && structureElement.properties) {
          let formattedDate = new Date(new Date(structureElement.properties.General.Collection.Date).toDateString())
          if (this.currentRegionIds.includes(structureElement.regionId)) {
            if (
              formattedDate >= startDate &&
              formattedDate <= endDate &&
              (
                // types.includes(structureElement.TypeId.toString()) ||
                isCluster)
            ) {
              counter = counter + 1
            }
          }
        }
      })
    })

    // this.assetManager.asset.RepairCampaigns.forEach((structure) => {
    //   structure.Elements.forEach((structureElement) => {
    //     if ((structureElement as StructuralElement).isExecuted) {
    //       let formattedDate = new Date(new Date(structureElement.properties.General.Collection.Date.toString().replace('Z', '')).toDateString())
    //       if (this.currentRegionIds.includes(structureElement.regionId)) {
    //         if (
    //           formattedDate >= startDate &&
    //           formattedDate <= endDate &&
    //           (
    //             // types.includes(structureElement.TypeId.toString()) ||
    //             isCluster)
    //         ) {
    //           counter = counter + 1
    //         }
    //       }
    //     }
    //   })
    // })
    return counter;
  }

  public protectionElementsFilterCount(startDate: Date, endDate: Date, types: String[], isCluster: boolean) {
    let counter = 0
    if (!Util.isNullOrUndefined(this.assetManager.asset?.ProtectionElementsCollection?.Elements)) {
      this.assetManager.asset.ProtectionElementsCollection.Elements?.forEach((tempProtectionElement) => {
        const protectionElement = tempProtectionElement as BasicElement//ProtectionElement
        if (protectionElement.TypeId !== "35d4384eae2e482abf11b5b81880cd5e" && protectionElement.properties) {
          let formattedDate = new Date(new Date(protectionElement?.createdAt).toDateString())
          if (this.currentRegionIds.includes(protectionElement?.regionId)) {
            if (
              formattedDate >= startDate &&
              formattedDate <= endDate &&
              (
                isCluster)
            ) {
              counter = counter + 1
            }
          }
        }
      })

      this.assetManager.asset.RepairCampaigns.forEach((structure) => {
        structure.Elements.forEach((protectionElement) => {
          let formattedDate = new Date(new Date(protectionElement.properties?.General?.Collection?.Date).toDateString())
          if (this.currentRegionIds.includes(protectionElement?.regionId)) {
            if (
              formattedDate >= startDate &&
              formattedDate <= endDate &&
              (
                isCluster)
            ) {
              counter = counter + 1
            }
          }
        })
      })
    }
    return counter;
  }

  public protectionElementsDateFilter(startDate: Date, endDate: Date, types: String[], isCluster: boolean, planId?: string) {

    let protectionElements: any = this.assetManager.asset.ProtectionElementsCollection.Elements
    for (let element of protectionElements) {
      let formattedDate = new Date(new Date(element.createdAt).toDateString(),
      )
      if (this.currentRegionIds.includes(element.regionId)) {
        if (
          formattedDate >= startDate &&
          formattedDate <= endDate &&
          types.includes(element.TypeId)
        ) {
          element.SurfaceDesign.visible = true
        } else {
          element.SurfaceDesign.visible = false
        }
      }
    }
  }

  public structureColorHighlight(startDate: Date, endDate: Date, types: String[], isCluster: boolean) {
    this.assetManager.asset.StructureCampaigns.forEach((structure) => {
      structure.Elements.forEach((structureElement) => {
        if (structureElement.TypeId !== "35d4384eae2e482abf11b5b81880cd5e" && structureElement.properties) {
          let formattedDate = new Date(new Date(structureElement.properties.General.Collection.Date).toDateString())
          if (this.currentRegionIds.includes(structureElement.regionId)) {
            if (
              formattedDate >= startDate &&
              formattedDate <= endDate &&
              (types.includes(structureElement.TypeId.toString()) || isCluster)
            ) {
              structureElement.SurfaceDesign.material = GraphicsMaterials.structureMaterial
              structureElement.SurfaceDesign.visible = true
              structureElement.isHighlightedByFilterStructure = true
            } else {
              structureElement.SurfaceDesign.material = structureElement.defaultMaterial
              structureElement.isHighlightedByFilterStructure = false
            }
          }
        }
      })
    })
    this.assetManager.asset.RepairCampaigns.forEach((repairCampaign) => {
      repairCampaign.Elements.forEach((repairStructureElement) => {
        if (repairStructureElement.TypeId !== "35d4384eae2e482abf11b5b81880cd5e" && repairStructureElement.properties) {
          let formattedDate = new Date(new Date(repairStructureElement.properties.General.Collection.Date.toString().replace('Z', '')).toDateString())
          if (this.currentRegionIds.includes(repairStructureElement.regionId)) {
            if (
              formattedDate >= startDate &&
              formattedDate <= endDate &&
              (types.includes(repairStructureElement.TypeId.toString()) || isCluster)
            ) {
              repairStructureElement.SurfaceDesign.material = GraphicsMaterials.structureMaterial
              repairStructureElement.SurfaceDesign.visible = true
              repairStructureElement.isHighlightedByFilterStructure = true
            } else {
              repairStructureElement.SurfaceDesign.material = repairStructureElement.defaultMaterial
              repairStructureElement.isHighlightedByFilterStructure = false
            }
          }
        }
      })
    })
  }

  public protectionElementsColorHighlight(startDate: Date, endDate: Date, types: String[], isCluster: boolean) {
    let protectionElements: any = this.assetManager.asset.ProtectionElementsCollection.Elements
    for (let element of protectionElements) {
      let formattedDate = new Date(
        new Date(element.createdAt).toDateString(),
      )
      if (this.currentRegionIds.includes(element.regionId)) {
        if (
          formattedDate >= startDate &&
          formattedDate <= endDate &&
          types.includes(element.TypeId)
        ) {
          element.SurfaceDesign.material = GraphicsMaterials.anodeHighlightMaterial
          element.SurfaceDesign.material.needsUpdate = true;
        }
      }
    }
  }

  public structureFilterReset() {
    this.assetManager.asset.StructureCampaigns.forEach((structure) => {
      structure.Elements.forEach((structureElement) => {
        structureElement.SurfaceDesign.material = structureElement.defaultMaterial;
        structureElement.SurfaceDesign.visible = true
        structureElement.isHighlightedByFilterStructure = false
      })
    })
    this.assetManager.asset.RepairCampaigns.forEach((repairCampaign) => {
      repairCampaign.Elements.forEach((repairStructureElement) => {
        repairStructureElement.SurfaceDesign.material = repairStructureElement.defaultMaterial;
        repairStructureElement.SurfaceDesign.visible = true
      })
    })
  }

  public protectionElementsFilterReset() {
    this.assetManager.asset.ProtectionElementsCollection.Elements.forEach((structureElement) => {
      structureElement.SurfaceDesign.material = structureElement.defaultMaterial;
      structureElement.SurfaceDesign.visible = true
      structureElement.isHighlightedByFilterStructure = false
    }
    )
    this.assetManager.asset.RepairCampaigns.forEach((repairCampaign) => {
      repairCampaign.Elements.forEach((repairStructureElement) => {
        repairStructureElement.SurfaceDesign.material = repairStructureElement.defaultMaterial;
        repairStructureElement.SurfaceDesign.visible = true
      })
    })
  }
  //#endregion

  //#region sensor
  public sensorDateFilter(startDate: Date, endDate: Date, types: String[], isCluster: boolean) {
    let sensorElements: any = this.assetManager.asset.SensorCampaign.Elements
    for (let element of sensorElements) {
      let formattedDate = new Date(
        new Date(element.sensorProperties.installationDate).toDateString(),
      )
      if (this.currentRegionIds.includes(element.regionId)) {
        if (
          formattedDate >= startDate &&
          formattedDate <= endDate &&
          types.includes(element.sensorProperties.sensorTypeId)
        ) {
          element.SurfaceDesign.visible = true
        } else {
          element.SurfaceDesign.visible = false
        }
      }
    }
  }

  public sensorColorHighlight(startDate: Date, endDate: Date, types: String[], isCluster: boolean) {
    let sensorElements: any = this.assetManager.asset.SensorCampaign.Elements
    for (let element of sensorElements) {
      let formattedDate = new Date(
        new Date(element.sensorProperties.installationDate).toDateString(),
      )
      if (this.currentRegionIds.includes(element.regionId)) {
        if (
          formattedDate >= startDate &&
          formattedDate <= endDate &&
          types.includes(element.sensorProperties.sensorTypeId)
        ) {
          element.SurfaceDesign.material = GraphicsMaterials.sensorHighlightMaterial
        } else {
          element.SurfaceDesign.material = GraphicsMaterials.sensorLowlightMaterial
        }
      }
    }
  }

  public sensorFilterResults(startDate: Date, endDate: Date, types: String[], isCluster: boolean) {
    let sensorElements: any = this.assetManager.asset.SensorCampaign.Elements
    let sensorCount = 0
    for (let element of sensorElements) {
      let formattedDate = new Date(
        new Date(element.sensorProperties.installationDate).toDateString(),
      )
      if (this.currentRegionIds.includes(element.regionId)) {
        if (
          formattedDate >= startDate &&
          formattedDate <= endDate &&
          types.includes(element.sensorProperties.sensorTypeId)
        ) {
          sensorCount++
        }
      }
    }
    return sensorCount
  }

  public sensorFilterReset() {
    let sensorElements: any = this.assetManager.asset.SensorCampaign.Elements
    for (let element of sensorElements) {
      element.SurfaceDesign.visible = true
      element.SurfaceDesign.material = (element as SurfaceElement).currentMaterial
    }
  }
  //#endregion


  public criticalAreaCount(startDate: Date, endDate: Date) {
    let elementsCount = 0
    this.assetManager.asset.CriticalAreaCampaigns.forEach((criticalAreaCollection) => {
      criticalAreaCollection.Elements.forEach((element) => {
        const criticalAreaElement = element as CriticalAreaElement
        let formattedDate = new Date(new Date(criticalAreaElement.CriticalAreaProperties.DateStarted).toDateString())
        if (this.currentRegionIds.includes(criticalAreaElement.regionId)) {
          if (
            formattedDate >= startDate &&
            formattedDate <= endDate
          ) {
            elementsCount++
          }
        }
      })
    })
    return elementsCount
  }

  public criticalAreaCountAll() {
    let elementsCount = 0
    this.assetManager.asset.CriticalAreaCampaigns.forEach((criticalAreaCollection) => {
      criticalAreaCollection.Elements.forEach((element) => {
        const criticalAreaElement = element as CriticalAreaElement
        let formattedDate = new Date(new Date(criticalAreaElement.CriticalAreaProperties.DateStarted).toDateString())
        if (this.currentRegionIds.includes(criticalAreaElement.regionId)) {
          elementsCount++
        }
      })
    })
    return elementsCount
  }

  public criticalAreaColorHighlight(startDate: Date, endDate: Date, inspectionIds: string[]) {
    this.assetManager.asset.CriticalAreaCampaigns.forEach((criticalAreaCollection) => {
      criticalAreaCollection.Elements.forEach((element) => {
        const criticalAreaElement = element as CriticalAreaElement
        let formattedDate = new Date(new Date(criticalAreaElement.CriticalAreaProperties.DateStarted).toDateString())
        let formattedEndDate = new Date(new Date(criticalAreaElement.CriticalAreaProperties.DateEnded).toDateString())
        if (this.currentRegionIds.includes(criticalAreaElement.regionId)) {
          if (
            formattedDate >= startDate &&
            formattedDate <= endDate && formattedEndDate <= this.assetManager.referenceDate
          ) {
            criticalAreaElement.SurfaceDesign.material = Util.isNullOrUndefined(inspectionIds) ? GraphicsMaterials.selectedBasicElementMaterial :
              inspectionIds.includes(criticalAreaElement.Guid) ? GraphicsMaterials.selectedBasicElementMaterial : (element as SurfaceElement).currentMaterial;
            criticalAreaElement.SurfaceDesign.visible = true
            criticalAreaElement.isHighlightedByFilterStructure = true
          } else {
            criticalAreaElement.SurfaceDesign.material = (element as SurfaceElement).currentMaterial
            criticalAreaElement.isHighlightedByFilterStructure = false
          }
        }
      })
    })
  }

  public async criticalAreaFilter(startDate: Date, endDate: Date): Promise<void> {
    const release = await this.selectionMutex.acquire();

    try {
      // this.graphics.outlinePass.selectedObjects = []
      // this.clearSectionSelected("", this.assetManager.asset) //sectionId, asset
      this.assetManager.asset.CriticalAreaCampaigns.forEach((criticalAreaCollection) => {
        criticalAreaCollection.Elements.forEach((element) => {
          const criticalAreaElement = element as CriticalAreaElement
          let formattedDate = new Date(new Date(criticalAreaElement.CriticalAreaProperties.DateStarted).toDateString())
          let formattedDateEnded = new Date(new Date(criticalAreaElement.CriticalAreaProperties.DateEnded).toDateString())
          var minDate = new Date('0001-01-01T00:00:00');
          if (this.currentRegionIds.includes(criticalAreaElement.regionId)) {
            if (
              (formattedDate >= startDate &&
                formattedDate <= endDate) && (criticalAreaElement.CriticalAreaProperties.DateEnded.toJSON() <= minDate.toJSON() || formattedDateEnded > this.assetManager.referenceDate)
            ) {
              //criticalAreaElement.SurfaceDesign.material = this.inspectionMaterial1
              criticalAreaElement.SurfaceDesign.visible = true
              this.graphics.removeOutlineElement(criticalAreaElement.SurfaceDesign);
            } else {
              criticalAreaElement.SurfaceDesign.visible = false
            }
          }
        })
      })
    } finally {
      release();
    }
  }

  clearSectionSelected(sectionId: string, asset: Asset): void {
    const sectionSelected = asset.GetVisibleRegions();
    if (sectionSelected) {
      for (const regionGroup of sectionSelected) {
        regionGroup.setBackToDefaultMaterial();
        this.materials.clearSectionAsSelected(regionGroup, sectionId);
      }
    }
    this.graphics.clearOutlinedElements()
    this.inspectionElementsHide()
    this.criticalAreaElementsHide()
  }

  public criticalAreaFilterReset() {
    this.assetManager.asset.CriticalAreaCampaigns.forEach((criticalAreaCollection) => {
      criticalAreaCollection.Elements.forEach((element) => {
        const gaugingElement = element as CriticalAreaElement
        gaugingElement.SurfaceDesign.visible = false
        gaugingElement.SurfaceDesign.material = (element as SurfaceElement).currentMaterial
        gaugingElement.isHighlightedByFilterStructure = false
      })
    })
  }

  public showCriticalAreas(ids: string[]) {
    const referenceDate = this.assetManager.referenceDate

    this.assetManager.asset.CriticalAreaCampaigns.forEach((criticalAreaCollection) => {
      criticalAreaCollection.Elements.forEach((element) => {
        const criticalAreaElement = element as CriticalAreaElement
        let formattedDate = new Date(new Date(criticalAreaElement.CriticalAreaProperties.DateStarted).toDateString())
        let formattedEndDate = new Date(new Date(criticalAreaElement.CriticalAreaProperties.DateEnded).toDateString())
        if (this.currentRegionIds.includes(criticalAreaElement.regionId)) {
          if (
            formattedDate <= referenceDate && formattedEndDate <= this.assetManager.referenceDate &&
            ids.includes(criticalAreaElement.CriticalAreaProperties.CriticalAreaId)
          ) {
            criticalAreaElement.SurfaceDesign.visible = true
          } else {
            criticalAreaElement.SurfaceDesign.visible = false
          }
        }
      })
    })
  }
  public showAllCriticalAreas(visible: boolean) {
    this.assetManager.asset.CriticalAreaCampaigns.forEach((criticalAreaCollection) => {
      criticalAreaCollection.Elements.forEach((element) => {
        const criticalAreaElement = element as CriticalAreaElement
        criticalAreaElement.SurfaceDesign.visible = visible
        criticalAreaElement.SurfaceDesign.material = (element as SurfaceElement).currentMaterial
      })
    })
  }

  public highlightCriticalAreas(ids: string[]) {
    const referenceDate = this.assetManager.referenceDate

    this.assetManager.asset.CriticalAreaCampaigns.forEach((criticalAreaCollection) => {
      criticalAreaCollection.Elements.forEach((element) => {
        const criticalAreaElement = element as CriticalAreaElement
        let formattedDate = new Date(new Date(criticalAreaElement.CriticalAreaProperties.DateStarted).toDateString())
        if (this.currentRegionIds.includes(criticalAreaElement.regionId)) {
          if (
            formattedDate <= referenceDate &&
            ids.includes(criticalAreaElement.CriticalAreaProperties.CriticalAreaId)
          ) {
            criticalAreaElement.SurfaceDesign.material = GraphicsMaterials.selectedBasicElementMaterial
            criticalAreaElement.SurfaceDesign.visible = true
            criticalAreaElement.isHighlightedByFilterStructure = true
          }
        }
      })
    })
  }

  public showCriticalAreasFromActionPlan(plan: Pick<IInspectionPlan, 'criticalAreaIds'>, showOnly: boolean) {
    this.assetManager.asset.CriticalAreaCampaigns.forEach((criticalAreaCollection) => {
      criticalAreaCollection.Elements.forEach((element) => {
        const criticalAreaElement = element as CriticalAreaElement
        const isInPlan = plan.criticalAreaIds.includes(criticalAreaElement.CriticalAreaProperties.CriticalAreaId);
        if (isInPlan) {
          criticalAreaElement.SurfaceDesign.visible = true
        }

        if (showOnly) {
          criticalAreaElement.SurfaceDesign.visible = isInPlan;
        }
      })
    })
  }

  public highlightCriticalAreasFromActionPlan(ids: string[]) {
    this.assetManager.asset.CriticalAreaCampaigns.forEach((criticalAreaCollection) => {
      criticalAreaCollection.Elements.forEach((element) => {
        const criticalAreaElement = element as CriticalAreaElement
        if (ids.includes(criticalAreaElement.CriticalAreaProperties.CriticalAreaId)) {
          criticalAreaElement.SurfaceDesign.material = GraphicsMaterials.selectedBasicElementMaterial
          criticalAreaElement.SurfaceDesign.visible = true
          criticalAreaElement.isHighlightedByFilterStructure = true
        } else {
          criticalAreaElement.SurfaceDesign.material = (element as SurfaceElement).currentMaterial
          // criticalAreaElement.SurfaceDesign.visible = false
          criticalAreaElement.isHighlightedByFilterStructure = false
        }
      })
    })
  }

}
